/**
 * Chat API service for VizDay unified conversation.
 */

// In production, React is served by Django from the same domain, so use relative URLs
// In development, use localhost:8000
const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

// Component data types - Base with common optional fields
interface ChatComponentDataBase {
  id?: number;
  title?: string;
}

export interface EventPreviewData extends ChatComponentDataBase {
  title: string;
  date?: string;
  start_time?: string;
  end_time?: string;
  location?: string;
  description?: string;
  attendees?: string[];
}

export interface ItineraryPreviewData extends ChatComponentDataBase {
  id: number;
  title: string;
  destination?: string;
  start_date: string;
  end_date?: string;
  items?: Array<{
    name: string;
    time?: string;
    location?: string;
  }>;
}

export interface CalendarSnippetData extends ChatComponentDataBase {
  date: string;
  events: Array<{
    title: string;
    time?: string;
    location?: string;
  }>;
}

export interface OptionsData extends ChatComponentDataBase {
  options: string[];
  question?: string;
}

export interface ItineraryLinkData extends ChatComponentDataBase {
  id: number;
  title: string;
  // `message`, which is the key the server actually sends
  // (`chat_service._execute_create_itinerary`). This said `share_token` — a
  // key nothing ever emitted — and the renderer passed it through as the
  // card's message, so the card rendered `undefined`. A phantom token surface
  // and a live rendering bug in one line.
  message?: string;
}

export type ChatComponentData =
  | EventPreviewData
  | ItineraryPreviewData
  | CalendarSnippetData
  | OptionsData
  | ItineraryLinkData;

// Types
export interface ChatComponent {
  type: 'event_preview' | 'itinerary_preview' | 'calendar_snippet' | 'options' | 'itinerary_link';
  data: ChatComponentData;
  action_id?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  components?: ChatComponent[];
  timestamp: string;
}

// Pending action data types based on action type
export interface PendingEventData {
  title: string;
  date?: string;
  start_time?: string;
  end_time?: string;
  location?: string;
  description?: string;
  attendees?: string[];
}

export interface PendingItineraryData {
  title: string;
  destination?: string;
  start_date: string;
  end_date?: string;
  items?: Array<{
    name: string;
    time?: string;
    location?: string;
    type?: string;
  }>;
}

export type PendingActionData = PendingEventData | PendingItineraryData | Record<string, unknown>;

export interface PendingAction {
  id: string;
  type: string;
  summary: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  data?: PendingActionData;
}

export interface ChatResponseData {
  text: string;
  components: ChatComponent[];
  suggestions: string[];
}

export interface ChatApiResponse {
  conversation_id: string;
  response: ChatResponseData;
  pending_actions: PendingAction[];
  provider?: string;
  model?: string;
  // Legacy fields (backward compat)
  soft_gate?: boolean;
  gate_type?: 'signup' | 'upgrade';
  // New unified gate fields
  gate?: 'auth' | 'upgrade' | null;
  msg_count?: number;
  msg_limit?: number;
}

export interface ChatModelOption {
  key: string;
  label: string;
  locked?: boolean;
}

export interface ConversationSummary {
  id: string;
  title: string;
  message_count: number;
  is_active: boolean;
  has_pending: boolean;
  updated_at: string;
}

export interface ConversationDetail {
  id: string;
  messages: ChatMessage[];
  pending_actions: PendingAction[];
  is_active: boolean;
  chat_model?: string;
  created_at: string;
  updated_at: string;
}

// Get CSRF token from cookie (set by Django)
function getCsrfToken(): string | null {
  const name = 'csrftoken';
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return cookieValue;
    }
  }
  return null;
}

// Helper function for API calls
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add CSRF token for mutating requests
  const method = options.method?.toUpperCase() || 'GET';
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
    }
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...headers,
      ...options.headers as Record<string, string>,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json();
}

export interface MatchMeAnswers {
  trip_types?: string[];
  distance?: string;
  group_type?: string;
}

/**
 * Send a message to the chat.
 */
export async function sendChatMessage(
  message: string,
  conversationId?: string | null,
  model?: string,
  context?: 'daily' | 'discovery' | 'planner',
  matchMeAnswers?: MatchMeAnswers | null,
): Promise<ChatApiResponse> {
  return fetchApi<ChatApiResponse>('/api/planner/chat/', {
    method: 'POST',
    body: JSON.stringify({
      message,
      conversation_id: conversationId || undefined,
      ...(model ? { model } : {}),
      ...(context ? { context } : {}),
      ...(matchMeAnswers ? { match_me_answers: matchMeAnswers } : {}),
    }),
  });
}

/**
 * Get available chat models.
 */
export async function getChatModels(): Promise<{ models: ChatModelOption[]; default: string }> {
  return fetchApi('/api/planner/chat/models/');
}

/**
 * Confirm a pending action.
 */
export async function confirmAction(
  conversationId: string,
  actionId: string
): Promise<ChatApiResponse> {
  return fetchApi<ChatApiResponse>('/api/planner/chat/', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: conversationId,
      confirm_action_id: actionId,
    }),
  });
}

/**
 * Cancel a pending action.
 */
export async function cancelAction(
  conversationId: string,
  actionId: string
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(
    `/api/planner/chat/${conversationId}/actions/${actionId}/cancel/`,
    { method: 'POST' }
  );
}

/**
 * Get a conversation by ID.
 */
export async function getConversation(
  conversationId: string
): Promise<ConversationDetail> {
  return fetchApi<ConversationDetail>(`/api/planner/chat/${conversationId}/`);
}

/**
 * List recent conversations.
 */
export async function listConversations(): Promise<{
  conversations: ConversationSummary[];
}> {
  return fetchApi<{ conversations: ConversationSummary[] }>(
    '/api/planner/chat/conversations/'
  );
}

/**
 * Delete a conversation.
 */
export async function deleteConversation(
  conversationId: string
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(
    `/api/planner/chat/${conversationId}/delete/`,
    { method: 'DELETE' }
  );
}

// Export default object for convenience
const chatApi = {
  sendMessage: sendChatMessage,
  confirmAction,
  cancelAction,
  getConversation,
  listConversations,
  deleteConversation,
  getChatModels,
};

export default chatApi;
